import { useLayoutEffect, useSyncExternalStore } from 'react'

type CapsLockState = boolean | null

let capsLockState: CapsLockState = null
const subscribers = new Set<() => void>()

const getSnapshot = () => capsLockState

const subscribe = (subscriber: () => void) => {
  subscribers.add(subscriber)

  return () => subscribers.delete(subscriber)
}

const setCapsLockState = (state: CapsLockState) => {
  if (capsLockState === state) return

  capsLockState = state
  subscribers.forEach((subscriber) => subscriber())
}

const readCapsLockState = (event: Event): CapsLockState => {
  if (!event.isTrusted) return null

  try {
    const { getModifierState } = event as KeyboardEvent

    return typeof getModifierState === 'function'
      ? getModifierState.call(event, 'CapsLock')
      : null
  } catch {
    return null
  }
}

const handleModifierEvent = (event: Event) => {
  const state = readCapsLockState(event)

  if (state !== null) setCapsLockState(state)
}

const resetCapsLockState = () => setCapsLockState(null)

const handleVisibilityChange = () => {
  if (document.visibilityState === 'hidden') resetCapsLockState()
}

const modifierEventTypes = [
  'keydown',
  'keyup',
  'pointerdown',
  'pointermove',
  'pointerup',
  'mousedown',
  'mousemove',
  'mouseup',
  'wheel',
]

export const CapsLockObserver = () => {
  useLayoutEffect(() => {
    modifierEventTypes.forEach((type) =>
      window.addEventListener(type, handleModifierEvent, true),
    )
    window.addEventListener('blur', resetCapsLockState)
    window.addEventListener('pagehide', resetCapsLockState)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      modifierEventTypes.forEach((type) =>
        window.removeEventListener(type, handleModifierEvent, true),
      )
      window.removeEventListener('blur', resetCapsLockState)
      window.removeEventListener('pagehide', resetCapsLockState)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      resetCapsLockState()
    }
  }, [])

  return null
}

export const useCapsLockState = () =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
